import React, { useState, useEffect } from 'react';
import { Container, Form, Row, Col, Button } from 'react-bootstrap';
import { ToastContainer, toast } from 'react-toastify';
import AppButton from '../../Components/MerchantButton';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import './UnifiedFormStyles.css';
import { compressAndUpload } from '../../Helpers/Utils/photoUpload';

const MayaSQRSolutionDetails = ({ navigation, primaryDetails, setPrimaryDetails }) => {
  // Initialize sqrItems array from primaryDetails or start empty
  const [sqrItems, setSqrItems] = useState(() => {
    if (primaryDetails.sqr_items && Array.isArray(primaryDetails.sqr_items) && primaryDetails.sqr_items.length > 0) {
      return primaryDetails.sqr_items;
    }
    return [];
  });

  // Current SQR being edited (not yet added to table)
  const [currentSqr, setCurrentSqr] = useState({
    sqr_store_code: '',
    sqr_store_code_unobtaining_reason: '',
    sqr_store_code_unobtaining_reason_text: '',
    has_sqr_concern: '',
    sqr_standee_location: '',
    sqr_count: '',
    sqr_updated: '',
    sqr_photo_temp_path: '',
    sqr_brand: '',
    sqr_unupdated_reason: ''
  });

  const [errors, setErrors] = useState({});

  const [isUploading, setIsUploading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      toast.error("Photo exceeds 5MB limit. Please retake the photo.", {
        autoClose: 3000, hideProgressBar: true, theme: "colored"
      });
      e.target.value = '';
      return;
    }

    setIsUploading(true);
    try {
      const uploadResult = await compressAndUpload(file);
      if (!uploadResult.success) {
        toast.error(uploadResult.error || "Upload failed. Please try again.");
        e.target.value = '';
        return;
      }
      handleSqrChange('sqr_photo_temp_path', uploadResult.temp_path);
    } catch (error) {
      toast.error("Error uploading photo");
    } finally {
      setIsUploading(false);
    }
  };

  // Sync sqrItems to primaryDetails whenever sqrItems change
  useEffect(() => {
    setPrimaryDetails((prev) => ({
      ...prev,
      sqr_items: sqrItems,
    }));
  }, [sqrItems, setPrimaryDetails]);

  // Handle current SQR field changes
  const handleSqrChange = (field, value) => {
    setCurrentSqr((prev) => ({
      ...prev,
      [field]: value
    }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: false }));
    }
    // Clear errors for conditional fields based on sqr_updated
    if (field === 'sqr_updated') {
      if (value === 'Yes') {
        setErrors((prev) => ({ ...prev, sqr_brand: false, sqr_unupdated_reason: false }));
      } else if (value === 'No') {
        setErrors((prev) => ({ ...prev, sqr_photo: false }));
      }
    }
  };

  // Add current SQR to table
  const handleAddSqr = () => {
    const newErrors = {};
    if (!currentSqr.sqr_store_code) newErrors.sqr_store_code = 'Required';
    if (!currentSqr.has_sqr_concern) newErrors.has_sqr_concern = 'Required';
    if (!currentSqr.sqr_standee_location) newErrors.sqr_standee_location = 'Required';
    if (!currentSqr.sqr_count) newErrors.sqr_count = 'Required';
    if (!currentSqr.sqr_updated) newErrors.sqr_updated = 'Required';
    if (currentSqr.sqr_updated === 'Yes' && !currentSqr.sqr_photo_temp_path) newErrors.sqr_photo = 'Required';
    if (currentSqr.sqr_updated === 'No' && !currentSqr.sqr_brand) newErrors.sqr_brand = 'Required';
    if (currentSqr.sqr_updated === 'No' && !currentSqr.sqr_unupdated_reason) newErrors.sqr_unupdated_reason = 'Required';
    if (currentSqr.sqr_store_code_unobtaining_reason === 'other reason' && !currentSqr.sqr_store_code_unobtaining_reason_text) newErrors.sqr_store_code_unobtaining_reason_text = 'Required';
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      toast.error('Please fill in all required fields', {
        autoClose: 3000,
        hideProgressBar: true,
        theme: 'colored',
      });
      return;
    }
    // Add to sqrItems array
    setSqrItems([...sqrItems, { ...currentSqr, id: Date.now() }]);
    // Reset current SQR form
    setCurrentSqr({
      sqr_store_code: '',
      sqr_store_code_unobtaining_reason: '',
      sqr_store_code_unobtaining_reason_text: '',
      has_sqr_concern: '',
      sqr_standee_location: '',
      sqr_count: '',
      sqr_updated: '',
      sqr_photo_temp_path: '',
      sqr_brand: '',
      sqr_unupdated_reason: ''
    });
    setErrors({});
    toast.success('SQR added successfully!');
  };

  const isFormValid = () => {
    if (sqrItems.length === 0) return false;

    return !sqrItems.some(item => {
      if (!item.sqr_store_code || !item.has_sqr_concern || !item.sqr_standee_location || !item.sqr_count || !item.sqr_updated) return true;
      if (item.sqr_updated === 'Yes' && !item.sqr_photo_temp_path) return true;
      if (item.sqr_updated === 'No' && (!item.sqr_brand || !item.sqr_unupdated_reason)) return true;
      if (item.sqr_store_code_unobtaining_reason === 'other reason' && !item.sqr_store_code_unobtaining_reason_text) return true;
      return false;
    });
  };
  // Remove SQR from table
  const handleRemoveSqr = (sqrId) => {
    setSqrItems(sqrItems.filter(s => s.id !== sqrId));
  };
  
  const options = [
    { key: '2.0', label: '2.0' },
    { key: '1.5', label: '1.5' },
    { key: 'old-design-green-bird', label: 'old design - green bird' },
    { key: 'custom', label: 'custom' },
    { key: 'p2p', label: 'P2P' },
  ];

  const handleBack = () => {
    if (navigation && navigation.previous) navigation.previous();
  };


  const handleNext = () => {
    // Validate all items in sqrItems before proceeding
    const missing = sqrItems.some(item => {
      if (!item.sqr_store_code || !item.has_sqr_concern || !item.sqr_standee_location || !item.sqr_count || !item.sqr_updated) return true;
      if (item.sqr_updated === 'Yes' && !item.sqr_photo_temp_path) return true;
      if (item.sqr_updated === 'No' && (!item.sqr_brand || !item.sqr_unupdated_reason)) return true;
      if (item.sqr_store_code_unobtaining_reason === 'other reason' && !item.sqr_store_code_unobtaining_reason_text) return true;
      return false;
    });
    if (missing || sqrItems.length === 0) {
      toast.error('Please fill in all required fields for all SQRs before proceeding.', {
        autoClose: 3000,
        hideProgressBar: true,
        theme: 'colored',
      });
      return;
    }
    if (navigation && navigation.go) navigation.go('merchandising');
  };


  return (
    <div className="form-bg">
      <Container>
        <ToastContainer />
        <Row className="justify-content-md-center">
          <Col md={10}>
            <div className="form-main-header">
              <h4 className="form-title">Maya SQR Solution Details</h4>
              <p className="form-desc">Please fill in all required details below</p>
            </div>
            <div className="form-main">
              <div className="form-main-content">
                <Form>
                  {/* Form Section for Current SQR */}
                  <div style={{ 
                    border: '2px solid #e0e0e0', 
                    borderRadius: '8px', 
                    padding: '20px', 
                    backgroundColor: '#f9f9f9',
                    marginBottom: '20px'
                  }}>
                    <h5 style={{ marginBottom: '20px', color: '#333' }}>
                      {sqrItems.length === 0 ? 'SQR No. 1' : `SQR No. ${sqrItems.length + 1}`}
                    </h5>

                    {/* SQR Store Code */}
                    <Form.Group className="mb-4">
                      <Form.Label className="form-label">
                        SQR Standee Store Codes <span className="form-required">*</span>
                      </Form.Label>
                      <Form.Text className="form-note-text d-block">
                        Remember to check "Powered by MAYA"
                      </Form.Text>
                      <Form.Control
                        type="text"
                        value={currentSqr.sqr_store_code || ''}
                        onChange={(e) => handleSqrChange('sqr_store_code', e.target.value)}
                        placeholder="047198, 096892"
                        className="form-input"
                      />
                    </Form.Group>

                    {/* Reason for not obtaining */}
                    <Form.Group className="mb-4">
                      <Form.Label className="form-label">Reason for not obtaining SQR store code</Form.Label>
                      <Form.Select
                        value={currentSqr.sqr_store_code_unobtaining_reason || ''}
                        onChange={(e) => handleSqrChange('sqr_store_code_unobtaining_reason', e.target.value)}
                        className="form-select"
                      >
                        <option value="">Select reason</option>
                        <option value="old design - green bird">old design - green bird</option>
                        <option value="old design - 1.5">old design - 1.5</option>
                        <option value="damaged standee">damaged standee</option>
                        <option value="misplaced">misplaced</option>
                        <option value="other reason">other reason</option>
                      </Form.Select>
                    </Form.Group>

                    {currentSqr.sqr_store_code_unobtaining_reason === 'other reason' && (
                      <Form.Group className="mb-4">
                        <Form.Label className="form-label">Please specify other reason</Form.Label>
                        <Form.Control
                          type="text"
                          value={currentSqr.sqr_store_code_unobtaining_reason_text || ''}
                          onChange={(e) => handleSqrChange('sqr_store_code_unobtaining_reason_text', e.target.value)}
                          placeholder="Enter other reason"
                          className="form-input"
                        />
                      </Form.Group>
                    )}

                    {/* Has SQR Concern */}
                    <Form.Group className="mb-4">
                      <Form.Label className="form-label">Has SQR Concern <span className="form-required">*</span></Form.Label>
                      <Form.Select
                        value={currentSqr.has_sqr_concern || ''}
                        onChange={(e) => handleSqrChange('has_sqr_concern', e.target.value)}
                        className="form-select"
                      >
                        <option value="">Select option</option>
                        <option value="Yes">Yes</option>
                        <option value="No">No</option>
                      </Form.Select>
                    </Form.Group>
                    
                    {/* SQR Location */}
                    <Form.Group className="mb-4">
                      <Form.Label className="form-label">
                        SQR Standee location <span className="form-required">*</span>
                      </Form.Label>
                      <Form.Select
                        value={currentSqr.sqr_standee_location || ''}
                        onChange={(e) => handleSqrChange('sqr_standee_location', e.target.value)}
                        className="form-select"
                      >
                        <option value="">Select location</option>
                        <option value="beside cashier">beside cashier</option>
                        <option value="others">others</option>
                      </Form.Select>
                    </Form.Group>

                    {/* SQR Count */}
                    <Form.Group className="mb-4">
                      <Form.Label className="form-label">
                        SQR Standee Count <span className="form-required">*</span>
                      </Form.Label>
                      <Form.Control
                        type="number"
                        value={currentSqr.sqr_count || ''}
                        onChange={(e) => handleSqrChange('sqr_count', e.target.value)}
                        placeholder="Enter count"
                        className="form-input"
                      />
                    </Form.Group>

                    {/* Updated SQR */}
                    <Form.Group className="mb-4">
                      <Form.Label className="form-label">Updated SQR Standee <span className="form-required">*</span></Form.Label>
                      {/* <Form.Text className="form-note-text d-block">
                        Updated SQR is the 3.0 version, color white, "SCAN NA ALL" SQR. Remember to check "Powered by MAYA"
                      </Form.Text> */}
                      <Form.Select
                        value={currentSqr.sqr_updated || ''}
                        onChange={(e) => handleSqrChange('sqr_updated', e.target.value)}
                        className="form-select"
                      >
                        <option value="">Select option</option>
                        <option value="Yes">Yes</option>
                        <option value="No">No</option>
                      </Form.Select>
                      <div>
                        <p className="form-note-text">
                          <em>"Note: Updated SQR is the 3.0 version, color white, "SCAN NA ALL" SQR"</em>
                        </p>
                      </div>
                    </Form.Group>

                    {currentSqr.sqr_updated === 'Yes' && (
                      <Form.Group className="mb-4">
                        <Form.Label className="form-label">
                          Photo of SQR <span className="form-required">*</span>
                          {currentSqr.sqr_photo_temp_path && !isUploading && (
                            <span style={{ marginLeft: '10px', color: '#28a745' }}>✅ Uploaded</span>
                          )}
                        </Form.Label>
                        <Form.Control
                          type="file"
                          accept="image/*"
                          onChange={handlePhotoUpload}
                          className={errors.sqr_photo ? 'form-input-error' : ''}
                          disabled={isUploading}
                        />
                        {isUploading && <div style={{ color: '#007bff', fontSize: '13px', marginTop: '6px' }}>⏳ Uploading...</div>}
                        {errors.sqr_photo && (
                          <div className="form-error">{errors.sqr_photo}</div>
                        )}
                      </Form.Group>
                    )}

                    {currentSqr.sqr_updated === 'No' && (
                      <>
                        <Form.Group className="mb-4">
                          <Form.Label className="form-label">SQR Standee Branding <span className="form-required">*</span></Form.Label>
                          {/* <Form.Text className="form-note-text d-block text-start mb-3">
                            Always check for "Powered by MAYA" at the bottom of the SQR.
                          </Form.Text> */}
                          <Form.Select
                            value={currentSqr.sqr_brand || ''}
                            onChange={(e) => handleSqrChange('sqr_brand', e.target.value)}
                            className="form-select"
                          >
                            <option value="">Select branding</option>
                            {options.map((opt) => (
                              <option key={opt.key} value={opt.key}>
                                {opt.label}
                              </option>
                            ))}
                          </Form.Select>
                          <div>
                            <p className="form-note-text">
                              <em>"Note: Always check for "POWERED BY MAYA" at the bottom of the SQR"</em>
                            </p>
                          </div>
                        </Form.Group>

                        <Form.Group className="mb-4">
                          <Form.Label className="form-label">
                            Reason SQR is not updated <span className="form-required">*</span>
                          </Form.Label>
                          <Form.Text className="form-note-text d-block text-start">1. Get insight why SQR is still not updated.</Form.Text>
                          <Form.Text className="form-note-text d-block text-start mb-2">2. Advise merchant to escalate request for SQR update.</Form.Text>
                          <Form.Control
                            type="text"
                            value={currentSqr.sqr_unupdated_reason || ''}
                            onChange={(e) => handleSqrChange('sqr_unupdated_reason', e.target.value)}
                            placeholder="Show Answer Text"
                            className="form-input"
                          />
                        </Form.Group>
                        <div>
                          <p className="form-note-text">
                            <em>"Note: 1. Get insight why SQR is still not updated
                                2. Advise merchant to escalate request for SQR update"</em>
                          </p>
                        </div>
                      </>
                    )}

                    {/* Add SQR Button */}
                    <div style={{ textAlign: 'center', marginTop: '20px' }}>
                      <Button 
                        variant="success" 
                        onClick={handleAddSqr}
                        style={{ display: 'flex', alignItems: 'center', gap: '8px', margin: '0 auto' }}
                      >
                        <PlusOutlined /> {isEditing ? 'Save SQR' : 'Add SQR'}
                      </Button>
                    </div>
                  </div>

                  {/* SQR Items Table */}
                  {sqrItems.length > 0 && (
                    <div className="mb-4">
                      <h6 className="form-label" style={{ marginBottom: '15px' }}>Added SQRs</h6>
                      <div style={{ overflowX: 'auto' }}>
                        <table style={{
                          width: '100%',
                          borderCollapse: 'collapse',
                          backgroundColor: '#fff',
                          border: '1px solid #ddd'
                        }}>
                          <thead>
                            <tr style={{ backgroundColor: '#f5f5f5', borderBottom: '2px solid #ddd' }}>
                              <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', borderRight: '1px solid #ddd' }}>No.</th>
                              <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', borderRight: '1px solid #ddd' }}>Store Code</th>
                              <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', borderRight: '1px solid #ddd' }}>Location</th>
                              <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', borderRight: '1px solid #ddd' }}>Count</th>
                              <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600', borderRight: '1px solid #ddd' }}>Updated</th>
                              <th style={{ padding: '12px', textAlign: 'center', fontWeight: '600' }}>Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {sqrItems.map((sqr, index) => (
                              <tr key={sqr.id} style={{ borderBottom: '1px solid #ddd' }}>
                                <td style={{ padding: '12px', borderRight: '1px solid #ddd' }}>{index + 1}</td>
                                <td style={{ padding: '12px', borderRight: '1px solid #ddd' }}>{sqr.sqr_store_code || '-'}</td>
                                <td style={{ padding: '12px', borderRight: '1px solid #ddd' }}>{sqr.sqr_standee_location || '-'}</td>
                                <td style={{ padding: '12px', borderRight: '1px solid #ddd' }}>{sqr.sqr_count || '-'}</td>
                                <td style={{ padding: '12px', borderRight: '1px solid #ddd' }}>{sqr.sqr_updated || '-'}</td>
                                <td style={{ padding: '12px', textAlign: 'center', display: 'flex', gap: '8px', justifyContent: 'center' }}>
                                  <Button 
                                    variant="primary" 
                                    size="sm"
                                    onClick={() => {
                                      setIsEditing(true);
                                      setCurrentSqr(sqr);
                                      handleRemoveSqr(sqr.id);
                                    }}
                                    style={{ display: 'inline-flex', alignItems: 'center', gap: '5px' }}
                                  >
                                    Edit
                                  </Button>
                                  {sqrItems.length > 1 && (
                                    <Button 
                                      variant="danger" 
                                      size="sm"
                                      onClick={() => handleRemoveSqr(sqr.id)}
                                      style={{ display: 'inline-flex', alignItems: 'center', gap: '5px' }}
                                    >
                                      <DeleteOutlined /> Remove
                                    </Button>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  <div className="form-nav-btns">
                    <AppButton onClick={handleBack} variant="back" className="form-btn variant-secondary">
                      ← Back
                    </AppButton>
                    <AppButton onClick={handleNext} variant="next" className="form-btn variant-primary" disabled={!isFormValid()}>
                      Next →
                    </AppButton>
                  </div>
                </Form>
              </div>
            </div>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default MayaSQRSolutionDetails;
